import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/Icon";
import { PlayButton } from "@/components/VideoModal";

const AUTOPLAY_MS = 7000;

// Splits a headline into sentences and colors the final one in the teal accent.
function Headline({ text }) {
  const parts = text.split(/\.\s*/).map((s) => s.trim()).filter(Boolean);
  return (
    <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
      {parts.map((part, i) => (
        <span key={i} className={i === parts.length - 1 ? "text-brand-teal2" : ""}>
          {part}.{i < parts.length - 1 && <br />}
        </span>
      ))}
    </h1>
  );
}

export function HeroCarousel({ content, heroSrc, odpSrc, onPlayVideo }) {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);

  const slides = [
    {
      key: "nemt",
      eyebrow: "Non-Emergency Medical Transportation • Pennsylvania",
      headline: content.hero_headline || "Reliable Rides. Compassionate Care. Every Time.",
      subtext: content.hero_subtext,
      primary: { label: "Book a Ride", testid: "hero-book-ride", to: "/book-a-ride", icon: true },
      secondary: { label: "Partner With Us", testid: "hero-partner", to: "/facilities" },
      bullets: ["Safe & Reliable Transportation", "Compassionate Drivers", "On-Time Service"],
    },
    {
      key: "odp",
      eyebrow: "ODP Services • Statewide Pennsylvania",
      headline: "Supporting Independence. Empowering Everyday Life.",
      subtext: "CareDeserved delivers person-centered ODP supports that help individuals with disabilities participate safely and confidently in their communities across Pennsylvania.",
      primary: { label: "Explore ODP Services", testid: "hero-odp-explore", to: "/odp-services", icon: true },
      secondary: { label: "Contact Our ODP Team", testid: "hero-odp-contact", to: "/contact" },
      bullets: ["Person-Centered Support", "Community Participation", "Statewide Pennsylvania"],
    },
  ];
  const count = slides.length;

  const go = useCallback((next, d) => { setDir(d); setIndex((next + count) % count); }, [count]);

  useEffect(() => {
    if (paused || reduce) return undefined;
    const t = setInterval(() => { setDir(1); setIndex((i) => (i + 1) % count); }, AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [paused, reduce, count]);

  const s = slides[index];

  const slideVar = {
    enter: (d) => ({ opacity: 0, x: reduce ? 0 : d > 0 ? 64 : -64 }),
    center: { opacity: 1, x: 0, transition: { duration: reduce ? 0 : 0.6, ease: [0.22, 1, 0.36, 1], staggerChildren: reduce ? 0 : 0.09, delayChildren: reduce ? 0 : 0.12 } },
    exit: (d) => ({ opacity: 0, x: reduce ? 0 : d > 0 ? -64 : 64, transition: { duration: reduce ? 0 : 0.35 } }),
  };
  const item = {
    enter: { opacity: 0, y: reduce ? 0 : 18 },
    center: { opacity: 1, y: 0, transition: { duration: reduce ? 0 : 0.5, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0 },
  };

  return (
    <section
      className="relative overflow-hidden bg-brand-blue"
      data-testid="hero-section"
      aria-roledescription="carousel"
      aria-label="CareDeserved highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
    >
      <AnimatePresence mode="wait" custom={dir} initial={false}>
        <motion.div
          key={s.key}
          custom={dir}
          variants={slideVar}
          initial="enter"
          animate="center"
          exit="exit"
          data-testid={`hero-slide-${s.key}`}
          className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:px-8 py-16 lg:min-h-[560px] lg:grid-cols-2 lg:gap-8 lg:py-24"
        >
          {/* Text column */}
          <div>
            <motion.p variants={item} className="eyebrow text-brand-teal2">{s.eyebrow}</motion.p>
            <motion.div variants={item}><Headline text={s.headline} /></motion.div>
            <motion.p variants={item} className="mt-6 max-w-xl text-base leading-relaxed text-slate-200 md:text-lg">{s.subtext}</motion.p>
            <motion.div variants={item} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button data-testid={s.primary.testid} onClick={() => navigate(s.primary.to)}
                className="h-14 rounded-full bg-brand-purple px-8 text-base font-semibold text-white transition-transform hover:-translate-y-0.5 hover:bg-brand-purple2">
                {s.primary.label} {s.primary.icon && <Icon name="ArrowRight" size={18} weight="bold" className="ml-2" />}
              </Button>
              <Button data-testid={s.secondary.testid} onClick={() => navigate(s.secondary.to)} variant="outline"
                className="h-14 rounded-full border-2 border-white/50 bg-white/5 px-8 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/15">
                {s.secondary.label}
              </Button>
            </motion.div>
            <motion.div variants={item} className="mt-9 flex flex-wrap gap-x-7 gap-y-3">
              {s.bullets.map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm font-medium text-slate-100">
                  <Icon name="CheckCircle" size={20} weight="fill" className="text-brand-teal2" /> {t}
                </div>
              ))}
            </motion.div>
          </div>

          {/* Image column */}
          <motion.div variants={item}>
            <div className="relative">
              <div className="overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/20">
                {s.key === "nemt" ? (
                  <>
                    <img src={heroSrc} data-testid="hero-image"
                      alt="CareDeserved caregiver compassionately assisting a passenger in a wheelchair beside a wheelchair-accessible transport van"
                      className="h-[300px] w-full object-cover sm:h-[400px] lg:h-[480px]" loading="eager" decoding="async" fetchpriority="high" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/40 to-transparent" />
                    <div className="absolute left-5 top-5 rounded-2xl bg-brand-blue/40 p-2 pr-4 backdrop-blur-md">
                      <PlayButton testid="hero-play" onClick={onPlayVideo} label="See how we care" sublabel="Watch video" />
                    </div>
                  </>
                ) : (
                  <>
                    <img src={odpSrc} data-testid="hero-odp-image"
                      alt="CareDeserved ODP support professional helping an individual participate confidently in their community"
                      className="h-[300px] w-full object-cover sm:h-[400px] lg:h-[480px]" loading="lazy" decoding="async" />
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/40 to-transparent" />
                  </>
                )}
              </div>
              <div className="absolute -bottom-5 -left-3 hidden items-center gap-3 rounded-2xl bg-white p-4 shadow-xl sm:flex">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
                  <Icon name={s.key === "nemt" ? "ShieldCheck" : "HandHeart"} size={24} weight="fill" />
                </span>
                <div>
                  <p className="font-heading text-sm font-bold text-slate-900">
                    {s.key === "nemt" ? "Care Beyond the Ride" : "Person-Centered Support"}
                  </p>
                  <p className="text-xs text-slate-500">
                    {s.key === "nemt" ? "Safety, dignity & comfort" : "Independence & everyday life"}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Arrows */}
      <button type="button" data-testid="hero-arrow-prev" aria-label="Previous slide"
        onClick={() => go(index - 1, -1)}
        className="absolute left-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/25 sm:flex lg:left-4">
        <CaretLeft size={22} weight="bold" />
      </button>
      <button type="button" data-testid="hero-arrow-next" aria-label="Next slide"
        onClick={() => go(index + 1, 1)}
        className="absolute right-2 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/25 sm:flex lg:right-4">
        <CaretRight size={22} weight="bold" />
      </button>

      {/* Dots */}
      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2.5" role="tablist" aria-label="Choose slide">
        {slides.map((sl, i) => (
          <button key={sl.key} type="button" role="tab" aria-selected={i === index}
            aria-label={`Go to ${sl.key === "nemt" ? "Transportation" : "ODP Services"} slide`}
            data-testid={`hero-dot-${i}`}
            onClick={() => go(i, i > index ? 1 : -1)}
            className={`h-2.5 rounded-full transition-all duration-300 ${i === index ? "w-8 bg-brand-teal2" : "w-2.5 bg-white/40 hover:bg-white/70"}`} />
        ))}
      </div>
    </section>
  );
}
